import { WORLD_CONFIG } from '../../../config/world.config';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, type CraneBeamHazard, type CraneSwingHazard } from '../../../hazards/HazardDefinition';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { hazardSocket, mirroredVariant, sidesWithout } from '../hazardBuilders';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLanes, entryConnector, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import { SegmentKind, type FixedObstaclePlacement, type ObstacleVariant, type SegmentDefinition } from '../SegmentDefinition';

/**
 * SEG_09 – crane area 40 m (design/01_segments/SEG_09_Crane_Area). Two tower cranes beside the
 * track work over it (moving hazards, see hazards/):
 *
 *   z 8    steel beams (OBS_CRANE_LOAD_A) across all lanes: hoisted high, after a warning lowered
 *          to 1.4 m, swinging along the track – slide under them (bottles under the beams). Over the
 *          outer lanes the beam ends lie under open sky (the sling closes over the centre lane only):
 *          a double jump clears them there (SEGMENT_STANDARD "Sprungehrlichkeit")
 *   z 28   concrete block (OBS_CRANE_LOAD_B): after a warning lowered to knee height over the track
 *          centre, then swinging across all three lanes like a pendulum (user 2026-10-02: "dass es
 *          von links nach rechts schwenkt … dass man hier ausweichen muss") – too low to slide under,
 *          its sling too high to jump over: be in a lane it is not over when you pass
 *   rows   z 18 and z 37 on the ground (pallets, concrete blocks, a lane closure)
 *
 * The swing is phase-locked to the runner's arrival (HazardsConfig.craneSwing); the variants differ
 * in where the block is when the runner gets there:
 *
 *   SWING_LEFT     turning point over lane 0 (it lingers there) · R18: L2 pallet (jump) · R37: L2 barrier (jump)
 *   SWING_RIGHT    mirror of SWING_LEFT
 *   SWING_THROUGH  sweeps through lane 1 towards the right – both outer lanes are free ·
 *                  R18: L0 lane closure (lane change) · R37: L0 concrete block (jump)
 *   SWING_THROUGH_MIRRORED  mirror (sweeps towards the left)
 */
export const CRANE_AREA = {
  length: 40,
  beam: { z: 8, clearance: 1.4, crane: { x: -13, z: 2 } },
  swing: { z: 28, crane: { x: 13, z: 34 } },
  rows: { R18: 18, R37: 37 },
} as const;

const W = WORLD_CONFIG;
const GROUND_TYPES = [ObstacleType.Jump, ObstacleType.Slide, ObstacleType.LaneChange] as const;
const SOCKET_BEAM = 'SOCKET_EVT_CRANE_BEAM';
const SOCKET_SWING = 'SOCKET_EVT_CRANE_SWING';
/** The block sweeps through the centre lane in half of the layouts' weight, it turns over an outer lane in the rest. */
const THROUGH_WEIGHT = 0.5;

const BEAM: CraneBeamHazard = {
  id: 'HAZ_CRANE_BEAM',
  kind: HazardKind.CraneBeam,
  socketId: SOCKET_BEAM,
  z: CRANE_AREA.beam.z,
  clearance: CRANE_AREA.beam.clearance,
  crane: CRANE_AREA.beam.crane,
};

const SWING_L0: CraneSwingHazard = {
  id: 'HAZ_CRANE_SWING_L0',
  kind: HazardKind.CraneSwing,
  socketId: SOCKET_SWING,
  lane: 0,
  z: CRANE_AREA.swing.z,
  crane: CRANE_AREA.swing.crane,
};
const SWING_L2: CraneSwingHazard = { ...SWING_L0, id: 'HAZ_CRANE_SWING_L2', lane: 2 };
const SWING_L1_RIGHT: CraneSwingHazard = { ...SWING_L0, id: 'HAZ_CRANE_SWING_L1_RIGHT', lane: 1, sweep: 1 };
const SWING_L1_LEFT: CraneSwingHazard = { ...SWING_L0, id: 'HAZ_CRANE_SWING_L1_LEFT', lane: 1, sweep: -1 };

const place = (row: keyof typeof CRANE_AREA.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({
  spawnPointId: `SOCKET_OBS_${row}_L${lane}`,
  obstacleId,
});

const SWING_LEFT: ObstacleVariant = {
  id: 'SWING_LEFT',
  weight: 1,
  placements: [place('R18', 2, 'OBS_PALLET_A'), place('R37', 2, 'OBS_BARRIER_A')],
  hazards: [BEAM.id, SWING_L0.id],
};

const SWING_THROUGH: ObstacleVariant = {
  id: 'SWING_THROUGH',
  weight: THROUGH_WEIGHT,
  // R18: a lane closure, no container – the beams at z 8 hang in the 13.7 m run-up a container roof needs
  // (level/terrain/roofRunUp.ts, user 2026-10-02); the lane change asked for stays the same.
  placements: [place('R18', 0, 'OBS_LANE_CLOSURE_A'), place('R37', 0, 'OBS_CONCRETE_BLOCK_A')],
  hazards: [BEAM.id, SWING_L1_RIGHT.id],
};

// The beam crane stands left in the first 20 m block, the swing crane right in the second one: their
// backdrop sockets stay empty, the roadside bays at the crane bases (6 m cross, turned towards the load)
// shrink to single ground props.
const SIDES = sidesWithout(CRANE_AREA.length, ['SOCKET_DECO_L_04', 'SOCKET_DECO_R_04_2'], ['SOCKET_DECO_L_01', 'SOCKET_DECO_R_02_2']);

export const SEG_CRANE_40M: SegmentDefinition = {
  id: 'SEG_CRANE_40M',
  kind: SegmentKind.Straight,
  displayName: 'Kranbereich 40 m',
  length: CRANE_AREA.length,
  width: W.trackWidth,
  visualAssetId: 'SEG_STRAIGHT_20M',
  extraVisuals: [{ assetId: 'SEG_STRAIGHT_20M', position: { x: 0, y: 0, z: 20 } }],

  entry: entryConnector(),
  exits: [straightExit(CRANE_AREA.length)],
  lanes: standardLanes(W),

  obstacleSpawnPoints: Object.entries(CRANE_AREA.rows).flatMap(([row, z]) => obstacleRow(W, row, z, GROUND_TYPES)),
  obstacleRowLayouts: { sparse: [], dense: [] },
  obstacleVariants: [
    SWING_LEFT,
    mirroredVariant(W, 'SWING_RIGHT', SWING_LEFT, [BEAM.id, SWING_L2.id]),
    SWING_THROUGH,
    mirroredVariant(W, 'SWING_THROUGH_MIRRORED', SWING_THROUGH, [BEAM.id, SWING_L1_LEFT.id]),
  ],
  hazards: [BEAM, SWING_L0, SWING_L2, SWING_L1_RIGHT, SWING_L1_LEFT],

  collectibleSpawnPoints: collectibleLanes(W, 1, CRANE_AREA.length - 1, 2, COLLECTIBLE_FLOAT_HEIGHT),

  decorationSockets: SIDES.decorationSockets,
  advertisingSlots: SIDES.advertisingSlots,
  dynamicEventSockets: [
    ...SIDES.dynamicEventSockets,
    hazardSocket(SOCKET_BEAM, 0, CRANE_AREA.beam.z, DynamicEventType.CraneLoadSwing),
    hazardSocket(SOCKET_SWING, 0, CRANE_AREA.swing.z, DynamicEventType.CraneLoadSwing),
  ],

  generation: {
    // A single module (the runs of whole crane areas were a misunderstanding, user 2026-10-03) – the swinging loads in a
    // row come from SEG_CRANE_SWING_20M.
    weight: 0.25,
    minDistance: 250,
    minSegmentsAfterJunction: 1,
    family: 'CRANE',
    tags: ['crane', 'hazard', 'slide', 'lane-change'],
  },
};
